/**
 * DP POLAR LAB — DP101 chapter 6. The envelope, and the three questions it must not be asked as one.
 *
 * ── THE CHAPTER IS ABOUT A MISTAKE THIS PROJECT MADE, SO THE LAB IS TOO ───────────────────────
 * §5: "The first verdict this engine produced said FAIL… The test was the wrong test." It had asked
 * whether she held the full intact criterion after her worst failure. She does not — 0 kn at 60° is
 * not 35 kn — so it failed a compliant vessel.
 *
 * That is not a historical anecdote here. While building the chapter-2 bench I wrote the same test,
 * for the same reason, and it awarded her class 1. The repair is §6's, and it is the transferable
 * part of the whole course: the question was three questions, and collapsing them produced a FALSE
 * NEGATIVE — wrong in the direction that feels safe, which the chapter points out is the dangerous
 * one, "because a check that fails safely-but-wrongly gets overridden, and an overridden check
 * protects nothing afterwards."
 *
 * So this lab does not draw a polar and leave the reader to admire it. It asks the three questions
 * side by side, with a separate computed answer under each, on whichever failure the learner picks.
 *
 * ── THE NUMBERS ARE THE CHAPTER'S OWN ─────────────────────────────────────────────────────────
 * Same engine, same declared criterion, same binary search as the server that produced the figures
 * printed in §4. Verified by gate-dp101-labs: after her worst case this computes 71.2 kn head to
 * weather and 0 kn at 60°, which is what the chapter publishes, to the decimal.
 */

import { useState, useEffect } from 'react';
import { Value } from '../../../packages/ankr-glass/src/react.jsx';
import { computed, cited, refused, absent } from '../../../packages/ankr-provenance/src/provenance.mjs';
import {
  assemble, activeFailures, worstCase, threeQuestions, criterionOf,
  benchEnvWhy, HS_FROM_WIND_NOTE, WHY_NOT_SCORED, loadCore,
} from './dplabs.mjs';

const SOURCE = 'DP101 chapter 6 — The Capability Plot, and What It Does Not Promise';

export default function DpPolarLab() {
  const [model, setModel] = useState(null);
  const [core, setCore] = useState(null);
  const [sel, setSel] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let live = true;
    Promise.all([fetch('/api/dp/model').then((r) => r.json()), loadCore()])
      .then(([m, c]) => { if (live) { setModel(m); setCore(c); } })
      .catch((e) => live && setErr(String(e && e.message || e)));
    return () => { live = false; };
  }, []);

  if (err) return <div className="g-glass shell-panel lab"><p className="g-body"><Value of={refused(`The plot needs her declared model and the DP engine: ${err}. Nothing is drawn rather than a stand-in envelope.`)} /></p></div>;
  if (!model || !core) return <div className="g-glass shell-panel lab"><p className="g-body g-dim">Loading her declared model and the engine…</p></div>;

  const built = assemble(model, [...(model.thrusters || []).map((t) => t.id), ...(model.generators || []).map((g) => g.id)]);
  const cands = activeFailures(built);
  const { worst } = worstCase(built, core, model);
  const chosen = sel === null ? worst : (sel === 'intact' ? { id: 'intact', label: 'no failure', dead: [], deadGenerators: [] } : cands.find((c) => c.id === sel));
  const intactQ = threeQuestions(built, core, model, [], []);
  const q = threeQuestions(built, core, model, chosen.dead, chosen.deadGenerators);
  const crit = criterionOf(model);

  /* THE PLOT IS DRAWN FROM THE COMPUTED POINTS, never from a smoothed curve — a spline between
     sixteen headings would invent the sixteen headings it passes through. Straight segments say
     exactly what was measured and nothing between. */
  const R = 92, CX = 110, CY = 110;
  const scale = Math.max(intactQ.best_max_wind_kn, 1);
  const ring = (pts) => pts.map((p) => {
    const rad = (p.alpha_deg - 90) * Math.PI / 180;
    const r = (Math.min(p.max_wind_kn ?? 0, scale) / scale) * R;
    return `${(CX + r * Math.cos(rad)).toFixed(1)},${(CY + r * Math.sin(rad)).toFixed(1)}`;
  }).join(' ');
  const critR = (Math.min(crit.wind_kn, scale) / scale) * R;

  const QUESTIONS = [
    { n: 1, q: 'Does she keep control?', tag: 'the class 2 question',
      answer: q.retains_control
        ? computed('YES — she can still hold a light environment on some heading')
        : refused('NO — she cannot hold even a light environment') },
    { n: 2, q: 'What envelope remains?', tag: 'the capability question',
      answer: computed(`${q.best_max_wind_kn} kn at ${q.best_heading_deg}° falling to ${q.worst_max_wind_kn} kn at ${q.worst_heading_deg}°`) },
    { n: 3, q: 'On some heading, or on every heading?', tag: 'the operating question',
      answer: computed(`${q.holds_criterion_any_heading ? 'some' : 'no'} heading${q.holds_criterion_every_heading ? ', and every heading' : ', but not every heading'} — against her ${crit.wind_kn} kn criterion`) },
  ];

  return (
    <div className="g-glass shell-panel lab">
      <div className="g-label">Capability plot · three questions, kept apart</div>
      <h2 className="g-h2 lab-h">The wrong test failed a compliant ship.</h2>

      <p className="g-body">
        <Value of={cited('Class 2 = no single fault causes loss of CONTROL. It never promised the intact envelope survives.', SOURCE)} />
      </p>
      <p className="g-body g-dim">
        Post-failure envelopes are smaller on every DP2 ship afloat — if they were not, capability
        plots would not publish in pairs at all.
      </p>

      <div className="lab-task">
        <div className="g-label">Choose a failure and read the envelope</div>
        <div className="lab-task-head dpf-picks">
          <button className={`g-btn ${chosen.id === 'intact' ? 'dpb-on' : ''}`} onClick={() => setSel('intact')}>intact</button>
          {cands.map((c) => (
            <button key={c.id} className={`g-btn ${chosen.id === c.id ? 'dpb-on' : ''} ${c.id === worst.id ? 'dpf-worst' : ''}`} onClick={() => setSel(c.id)}>
              {c.id.replace(/^(thruster|generator|switchboard):/, '')}
            </button>
          ))}
        </div>
        <p className="g-body g-dim">Opens on her computed worst case. {chosen.label}.</p>

        <svg viewBox="0 0 220 220" className="dpp-plot" role="img" aria-label="capability envelope">
          <circle cx={CX} cy={CY} r={R} className="dpp-grid" />
          <circle cx={CX} cy={CY} r={critR} className="dpp-crit" />
          <line x1={CX} y1={CY - R} x2={CX} y2={CY + R} className="dpp-grid" />
          <line x1={CX - R} y1={CY} x2={CX + R} y2={CY} className="dpp-grid" />
          <polygon points={ring(intactQ.points)} className="dpp-intact" />
          {chosen.id !== 'intact' && <polygon points={ring(q.points)} className="dpp-after" />}
          <text x={CX} y={12} className="dpp-lab">0° head to weather</text>
        </svg>
        <p className="g-body g-dim dpp-key">
          <span className="dpp-k-intact">outer</span> intact · <span className="dpp-k-after">inner</span> after
          the chosen failure · <span className="dpp-k-crit">dashed</span> her {crit.wind_kn} kn criterion.
          Straight segments between the {intactQ.points.length} computed headings — nothing is smoothed,
          because a curve would invent the headings it passes through.
        </p>
      </div>

      {/* ── THE THREE QUESTIONS ── */}
      <div className="lab-task">
        <div className="g-label">Ask them separately and each has a clean answer</div>
        {QUESTIONS.map((x) => (
          <div key={x.n} className="dpp-q">
            <b>{x.n}. {x.q}</b> <span className="g-dim">— {x.tag}</span>
            <p className="g-body"><Value of={x.answer} /></p>
          </div>
        ))}
        <p className="g-body dpp-note">
          <Value of={cited('The original test collapsed the second and third into the first and got a false negative.', SOURCE)} />
        </p>
        <p className="g-body g-dim">
          A false negative felt safe and was not: a check that fails safely-but-wrongly gets
          overridden, and an overridden check protects nothing afterwards.
        </p>
      </div>

      {/* ── WHAT THE OPERATOR DOES WITH IT ── */}
      {chosen.id !== 'intact' && (
        <div className="lab-task dpb-verdict">
          <div className="g-label">What this means on the bridge</div>
          <p className="g-body">
            Her working limit after this failure is a heading band, not a wind speed:{' '}
            <Value of={computed(`${q.best_max_wind_kn} kn at ${q.best_heading_deg}°, ${q.worst_max_wind_kn} kn at ${q.worst_heading_deg}°`)} />
          </p>
          <p className="g-body">
            <Value of={cited('A vessel required to hold a FIXED heading alongside a structure is limited by the worst figure, not the best.', SOURCE)} />
          </p>
          <p className="g-body g-dim">
            Which is why “come head to weather” is the standing answer to a degraded plant. It is not
            seamanlike instinct — it is where the post-failure envelope is largest, and you have just
            computed it.
          </p>
        </div>
      )}

      <p className="g-body g-dim">{benchEnvWhy(model)}</p>
      <p className="g-body g-dim">{HS_FROM_WIND_NOTE}</p>
      <p className="g-body g-dim lab-foot">{WHY_NOT_SCORED}</p>
      <p className="g-body g-dim"><Value of={absent()} /> Nothing on this plot is an attestation.</p>
    </div>
  );
}
